/**
 *
 * @notes
 *
 */
import React, {Component} from "react"
import PropTypes from "prop-types"
import {Row, Col} from "react-bootstrap"
import querystring from "querystring"
import {isEqual, isNil, omitBy, get} from "lodash"

import pbId from "@irati/utils/id"
import {CycleQuery, CycleResponseCountQuery} from "@irati/insights/apollo/query/schedule"
import TeamsQuery from "@irati/identity/apollo/query/Teams"
import authentication from "@irati/auth"
import AnalysisContext from "@irati/insights/context/AnalysisContext"
import history from "@irati/utils/history"
import {extractFiltersFromLocation} from "@irati/insights/utils/analysis"
import {DemoTenantContext} from "@irati/insights/context"
import {overrideAuth} from "@irati/insights/utils/apollo"
import {TeamState} from "@irati/types"

import AnalysisResponseCountChecker from "./AnalysisResponseCountChecker"
import {Breadcrumbs} from "../Navigation"
import {ListFilter} from "../ListFilter"
import sitemap from "../../routes/sitemap"
import {Page} from "../Layout"

class AnalysisNavigation extends Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired
  }

  constructor(props) {
    super(props)

    this.state = {
      locationFilters: extractFiltersFromLocation(props.location)
    }
  }

  componentDidUpdate(prevProps) {
    const {location: prevLocation} = prevProps
    const {location} = this.props

    const prevFilters = extractFiltersFromLocation(prevLocation)
    const filters = extractFiltersFromLocation(location)

    /** Disabling lint because it is inside a conditional */
    /* eslint-disable react/no-did-update-set-state */
    if (!isEqual(prevFilters, filters)) {
      this.setState({locationFilters: filters})
    }
    /* eslint-enable react/no-did-update-set-state */
  }

  getCycleQueryVariables(tenant) {
    const {match: {params} = {}} = this.props
    const {scheduleId: id} = params
    return {
      tenant,
      id,
      withDrivers: true,
      withSurveyCount: true,
      withNthSurvey: true,
      withInterest: true,
      nthSurveyIndex: -2
    }
  }

  getResponseCountQueryVariables(schedule) {
    const {locationFilters} = this.state
    const {time: minOpenedAt, teamId: team} = locationFilters
    const {id, tenant} = schedule

    return {
      tenant,
      id,
      team,
      minOpenedAt
    }
  }

  handleFilterChange = ({driverId, teamId, timeId, time}) => {
    const {location} = this.props

    let search = omitBy({driverId, teamId, timeId, time}, isNil)
    search = querystring.stringify(search)
    if (search) search = `?${search}`

    history.replace({
      pathname: location.pathname,
      search
    })
  }

  render() {
    const idNamespace = pbId("AnalysisNavigation", this.props)
    const {match} = this.props
    const {params} = match
    const {locationFilters} = this.state

    return (
      <DemoTenantContext.Consumer>
        {({cycleUuid, tenantUuid, accessToken: demoTenantAccessToken}) => {
          const isDemo = params.scheduleId === cycleUuid
          const tenantId = isDemo ? tenantUuid : authentication.userTenant().uuid

          return (
            <CycleQuery variables={this.getCycleQueryVariables(tenantId)} {...overrideAuth(demoTenantAccessToken, isDemo)}>
              {({schedule}) => {
                const isSendToAll = !!schedule.preferences.sendToAll
                return (
                  <TeamsQuery
                    skip={!isSendToAll}
                    variables={{tenantId: schedule.tenant, state: TeamState.Generic}}
                    {...overrideAuth(demoTenantAccessToken, isDemo)}>
                    {({data: {teams: teamsData} = {}}) => {
                      const {results: teams} = teamsData || []

                      /** Determine breadcrumbs */
                      const crumbs = [sitemap.homepage, {...sitemap.analysis, values: {schedule: schedule.reference}}]

                      /** Complete team list */
                      const teamList = isSendToAll ? teams : schedule.teamsSentTo.results
                      const drivers = get(schedule, "drivers.results")

                      return (
                        <Page id={idNamespace}>
                          <Breadcrumbs id={idNamespace} crumbs={crumbs} match={match} />
                          <Row>
                            <Col xs={12}>
                              <ListFilter
                                id={idNamespace}
                                onChange={this.handleFilterChange}
                                hasTime
                                values={locationFilters}
                                teams={teamList}
                                drivers={drivers.length > 1 ? drivers : null} />
                            </Col>
                          </Row>

                          <CycleResponseCountQuery
                            variables={this.getResponseCountQueryVariables(schedule)}
                            {...overrideAuth(demoTenantAccessToken, isDemo)}>
                            {({responseCount}) => (
                              <AnalysisContext.Provider
                                value={{
                                  schedule,
                                  responseCount,
                                  filters: locationFilters,
                                  teams: teamList,
                                  isDemo,
                                  tenantId,
                                  demoTenantAccessToken
                                }}>
                                <AnalysisResponseCountChecker id={idNamespace} />
                              </AnalysisContext.Provider>
                            )}
                          </CycleResponseCountQuery>
                        </Page>
                      )
                    }}
                  </TeamsQuery>
                )
              }}
            </CycleQuery>
          )
        }}
      </DemoTenantContext.Consumer>
    )
  }
}

export default AnalysisNavigation
